import Modal from "@via/components/modal";
import { ModalSize } from "@via/utils/constants";
import { FieldFormData } from "@via/pages/data/premium-dataset/utils/helper";
import {
  type FC,
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from "react";
import AdditionalFieldForm from "./additional-field-form";
import {
  InformationCircleIcon,
  PlusIcon,
  MinusIcon,
} from "@heroicons/react/24/outline";

interface ManualAddFieldsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFieldsCreated: (createdFields: FieldFormData[]) => void;
}

const ManualAddFieldsModal: FC<ManualAddFieldsModalProps> = ({
  isOpen,
  onClose,
  onFieldsCreated,
}) => {
  const [fieldsCount, setFieldsCount] = useState(1);
  const [fieldDataMap, setFieldDataMap] = useState<
    Record<string, FieldFormData>
  >({});
  const [isCreating, setIsCreating] = useState(false);
  const fieldDataMapRef = useRef<Record<string, FieldFormData>>({});

  // Generate field names based on count
  const generateFieldNames = useCallback((count: number): string[] => {
    return Array.from({ length: count }, (_, index) => `Field ${index + 1}`);
  }, []);

  const fieldNames = useMemo(
    () => generateFieldNames(fieldsCount),
    [fieldsCount, generateFieldNames]
  );

  const [selectedFields, setSelectedFields] = useState<Set<string>>(
    new Set(fieldNames)
  );

  // Update selectedFields when fieldNames change
  useEffect(() => {
    setSelectedFields(new Set(fieldNames));
  }, [fieldNames]);

  // Handle field data changes from individual forms
  const handleFieldDataChange = useCallback(
    (fieldName: string, data: FieldFormData) => {
      fieldDataMapRef.current = {
        ...fieldDataMapRef.current,
        [fieldName]: data,
      };
      setFieldDataMap(fieldDataMapRef.current);
    },
    []
  );

  // Handle field selection toggle
  const handleFieldSelection = useCallback(
    (fieldName: string, isSelected: boolean) => {
      setSelectedFields(prev => {
        const newSet = new Set(prev);
        if (isSelected) {
          newSet.add(fieldName);
        } else {
          newSet.delete(fieldName);
        }
        return newSet;
      });
    },
    []
  );

  // Handle field count changes
  const handleFieldsCountChange = useCallback(
    (newCount: number) => {
      if (newCount < 1) return;
      if (newCount > 10) return; // Limit to 10 fields for performance

      setFieldsCount(newCount);

      // Clean up field data for removed fields
      const newFieldNames = generateFieldNames(newCount);
      const newFieldDataMap = { ...fieldDataMapRef.current };
      const newSelectedFields = new Set<string>();

      // Remove data for fields that no longer exist
      Object.keys(newFieldDataMap).forEach(fieldName => {
        if (!newFieldNames.includes(fieldName)) {
          delete newFieldDataMap[fieldName];
        }
      });

      // Update selected fields to only include existing fields
      selectedFields.forEach(fieldName => {
        if (newFieldNames.includes(fieldName)) {
          newSelectedFields.add(fieldName);
        }
      });

      fieldDataMapRef.current = newFieldDataMap;
      setFieldDataMap(newFieldDataMap);
      setSelectedFields(newSelectedFields);
    },
    [generateFieldNames, selectedFields]
  );

  const handleCreateFields = async () => {
    setIsCreating(true);
    try {
      // Only create fields that are selected
      const fieldsToCreate = Array.from(selectedFields)
        .map(fieldName => fieldDataMap[fieldName])
        .filter(
          fieldData =>
            fieldData && fieldData.name && fieldData.label && fieldData.type
        );

      await onFieldsCreated(fieldsToCreate);
    } finally {
      setIsCreating(false);
    }
  };

  // Check if selected fields have valid data
  const selectedFieldsValid = Array.from(selectedFields).every(fieldName => {
    const fieldData = fieldDataMap[fieldName];
    return fieldData && fieldData.name && fieldData.label && fieldData.type;
  });

  const hasSelectedFields = selectedFields.size > 0;

  // Reset state when modal closes
  const handleClose = useCallback(() => {
    setFieldsCount(1);
    setFieldDataMap({});
    setSelectedFields(new Set());
    setIsCreating(false);
    fieldDataMapRef.current = {};
    onClose();
  }, [onClose]);

  return (
    <Modal
      open={isOpen}
      setOpen={handleClose}
      title="Add New Fields"
      size={ModalSize.Large}
      showButtons={false}
      disableClose={isCreating}>
      <div className="flex h-[70vh] flex-col">
        {/* Header Information - Fixed */}
        <div className="flex-shrink-0 rounded-lg bg-green-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <InformationCircleIcon className="h-5 w-5 text-green-500" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Add New Fields
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>
                  Create new fields for your Sample Tracker. Configure the field
                  properties and select which fields you want to create.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Field Count Control - Fixed */}
        <div className="flex-shrink-0 border-b border-gray-200 pb-4 pt-6">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-medium text-gray-900">
              Number of Fields to Add
            </h4>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => handleFieldsCountChange(fieldsCount - 1)}
                disabled={fieldsCount <= 1}
                className="inline-flex items-center rounded-md bg-white px-2 py-1 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                <MinusIcon className="h-4 w-4" />
              </button>
              <span className="text-sm font-medium text-gray-900 min-w-[2rem] text-center">
                {fieldsCount}
              </span>
              <button
                type="button"
                onClick={() => handleFieldsCountChange(fieldsCount + 1)}
                disabled={fieldsCount >= 10}
                className="inline-flex items-center rounded-md bg-white px-2 py-1 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Field Forms */}
        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-4 pr-2">
            {fieldNames.map(fieldName => (
              <AdditionalFieldForm
                key={fieldName}
                columnName={fieldName}
                onFieldDataChange={data =>
                  handleFieldDataChange(fieldName, data)
                }
                initialData={fieldDataMap[fieldName]}
                isSelected={selectedFields.has(fieldName)}
                onSelectionChange={isSelected =>
                  handleFieldSelection(fieldName, isSelected)
                }
              />
            ))}
          </div>
        </div>

        {/* Action Buttons - Fixed */}
        <div className="flex-shrink-0 flex justify-end space-x-3 border-t border-gray-200 pt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={isCreating}
            className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50">
            Cancel
          </button>

          <button
            type="button"
            onClick={handleCreateFields}
            disabled={!hasSelectedFields || !selectedFieldsValid || isCreating}
            className="inline-flex items-center rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50">
            {`Create ${selectedFields.size} Selected Field${selectedFields.size !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ManualAddFieldsModal;
